import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import { keyframes } from "@emotion/react";

import CloseIcon from "@mui/icons-material/CloseRounded";

import { DYNAMIC_BASE_URL } from "shared/enum";

type PreviewOptions = {
  open: boolean;
  title: string;
  imgPath?: string;
  videoPath?: string;
};

type FeaturePreviewProps = {
  options: PreviewOptions;
  handleClose: () => void;
};

export default function FeaturePreview({ options, handleClose }: FeaturePreviewProps) {
  const [isOpen, setIsOpen] = useState(options.open);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(handleClose, 300);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container position="absolute" top={0} left={0} open={isOpen}>
      <IconButton
        className="close-button"
        aria-label="close"
        size="large"
        onClick={() => setIsOpen(false)}
      >
        <CloseIcon fontSize="inherit" />
      </IconButton>

      {!!options.videoPath && (
        <Video src={DYNAMIC_BASE_URL + options.videoPath} autoPlay loop muted />
      )}
      {!!options.imgPath && <Image src={DYNAMIC_BASE_URL + options.imgPath} alt={options.title} />}
    </Container>
  );
}

const Video = styled("video")`
  border-radius: 4px;
  width: 100%;
  height: 100%;
`;

const Image = styled("img")`
  border-radius: 4px;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const fadeInAnimation = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const fadeOutAnimation = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const comeIntoFrame = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const leaveFrame = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }

  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

type ContainerProps = {
  open: boolean;
};

const Container = styled(Box)<ContainerProps>`
  width: 100%;
  height: 100%;
  opacity: 0;
  z-index: ${({ theme }) => theme.zIndex.drawer + 1};
  background-color: ${({ theme }) => theme.palette.background.default};
  animation: ${({ open }) => (open ? fadeInAnimation : fadeOutAnimation)} 0.3s ease-in-out forwards;

  & > .close-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    transform: translateX(100%);
    cursor: pointer;
    animation: ${({ open }) => (open ? comeIntoFrame : leaveFrame)} 0.3s ease-in-out forwards;
  }
`;
